import { useEffect, useState } from "react";

// Format an ISO timestamp as a short relative string ("just now", "12s
// ago", "3m ago", "5h ago", or the locale date). Same vocabulary the
// individual builders had inline — now centralized so RelativeTime can
// auto-refresh callers.
export function relativeTimeString(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const ms = Date.now() - t;
  if (ms < 0) return "in the future";
  if (ms < 2_000) return "just now";
  if (ms < 60_000) return `${Math.floor(ms / 1_000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}

// Decide how often a timestamp needs to re-render. Newer = faster.
// Caps at 60s for anything older than 1 hour.
function refreshIntervalMs(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 1_000;        // every second while < 1 min
  if (ms < 3_600_000) return 30_000;    // every 30s while < 1 hour
  return 60_000;                        // hourly tick once it's older
}

// Self-updating relative timestamp. Renders as a <time> element with the
// absolute time in the title so hover still reveals the precise moment.
export default function RelativeTime({
  iso,
  className = "",
  prefix = "",
}: { iso: string; className?: string; prefix?: string }) {
  const [, force] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const schedule = () => {
      const wait = refreshIntervalMs(iso);
      const id = window.setTimeout(() => {
        if (cancelled) return;
        force((n) => n + 1);
        schedule();
      }, wait);
      return id;
    };
    const id = schedule();
    return () => { cancelled = true; window.clearTimeout(id); };
  }, [iso]);

  const abs = new Date(iso);
  const title = Number.isFinite(abs.getTime()) ? abs.toLocaleString() : iso;
  return (
    <time dateTime={iso} title={title} className={className}>
      {prefix}{relativeTimeString(iso)}
    </time>
  );
}
